// Main application bootstrap with Alpine.js
// @ts-expect-error Alpine global
import Alpine from 'alpinejs';
import { Router } from './app/router';
import { ScreenController } from './ui/screenController';
import { store } from './app/store';
import { initAnalytics, clearAnalyticsBuffer, enableAnalyticsIdentity } from './app/analytics';
import { initStatsig } from './app/statsig';
import { initAmplitude } from './app/amplitude';
import { initGoogleTag } from './app/googleTag';
import { initMetaPixel } from './infra/metaPixel';
import localeEn from './config/locale.en.json';
import localeRu from './config/locale.ru.json';
import localeEs from './config/locale.es.json';
import localeFr from './config/locale.fr.json';
import localeDe from './config/locale.de.json';
import localePt from './config/locale.pt.json';
import { waitForConsent } from './utils/consent';
import { initConsentAlpineData } from './domain/consent';
import { PreloaderService } from './utils/preloader';
import { FLOW_CONFIG } from './config/flow';
import { initExperiments, getActiveExperiments } from './config/experiments';

// Available locales
const locales: Record<string, typeof localeEn> = {
  en: localeEn,
  ru: localeRu,
  es: localeEs,
  fr: localeFr,
  de: localeDe,
  pt: localePt,
};

// Load locale by code (returns English as fallback)
function getLocale(code: string): typeof localeEn {
  return locales[code] || locales.en;
}

// Make Alpine available globally
window.Alpine = Alpine;

// Globe is loaded lazily via PreloaderService (afterAppLoaded)
declare global {
  interface Window {
    initGlobe: ((el: HTMLElement) => void) | null;
    Alpine: any;
    lunaiDebug: any;
  }
}
window.initGlobe = null; // Will be populated by preloader when Globe loads

// Preserve static HTML text when localization misses a key
document.addEventListener('alpine:init', () => {
  Alpine.directive('text', (el, { expression }, { effect, evaluateLater }) => {
    const evaluate = evaluateLater(expression);
    const defaultText = el.textContent ?? '';
    effect(() => {
      evaluate((value: unknown) => {
        if (value === undefined || value === null || value === '') {
          el.textContent = defaultText;
        } else {
          el.textContent = String(value);
        }
      });
    });
  });

  // Preserve static HTML when localization misses a key (for x-html)
  Alpine.directive('html', (el, { expression }, { effect, evaluateLater }) => {
    const evaluate = evaluateLater(expression);
    const defaultHtml = el.innerHTML ?? '';
    effect(() => {
      evaluate((value: unknown) => {
        if (value === undefined || value === null || value === '') {
          el.innerHTML = defaultHtml;
        } else {
          el.innerHTML = String(value);
        }
      });
    });
  });

  // Preserve static HTML when localization misses a key (for x-html)
  Alpine.directive('placeholder', (el, { expression }, { effect, evaluateLater }) => {
    const evaluate = evaluateLater(expression);
    const defaultPlaceholder = el.placeholder ?? '';
    effect(() => {
      evaluate((value: unknown) => {
        if (value === undefined || value === null || value === '') {
          el.placeholder = defaultPlaceholder;
        } else {
          el.placeholder = String(value);
        }
      });
    });
  });

  // Language dropdown component
  Alpine.data('languageDropdown', () => ({
    open: false,
    languages: [
      { code: 'en', name: 'English', flag: '🇺🇸', flagGb: '🇬🇧' },
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
      { code: 'pt', name: 'Português', flag: '🇧🇷' },
      { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    ],
    selected: 'en',

    init() {
      // Restore saved language or detect from browser
      const saved = store.get<string>('language');
      if (saved) {
        this.selected = saved;
      } else {
        // Detect language from browser and use if supported
        const browserLang = (navigator.language || '').toLowerCase();
        const langCode = browserLang.split('-')[0]; // Get base language (e.g., 'en' from 'en-GB')
        const supported = this.languages.find(l => l.code === langCode);
        if (supported) {
          this.selected = supported.code;
        }
      }

      // Listen for language changes from other dropdown instances
      store.on((state) => {
        const lang = state.language as string | undefined;
        if (lang && lang !== this.selected) {
          this.selected = lang;
        }
      });
    },

    get currentFlag(): string {
      const lang = this.languages.find(l => l.code === this.selected);
      if (!lang) return '🇺🇸';
      if (lang.code === 'en') {
        return this.isGbLocale() ? (lang.flagGb || lang.flag) : lang.flag;
      }
      return lang.flag;
    },

    get currentCode(): string {
      return this.selected.toUpperCase();
    },

    isGbLocale(): boolean {
      const browserLang = (navigator.language || '').toLowerCase();
      return browserLang.includes('gb') || browserLang.includes('uk');
    },

    getFlag(lang: { code: string; flag: string; flagGb?: string }): string {
      if (lang.code === 'en') {
        return this.isGbLocale() ? (lang.flagGb || lang.flag) : lang.flag;
      }
      return lang.flag;
    },

    async select(code: string) {
      this.selected = code;
      store.set('language', code);
      this.open = false;

      // Load and apply new locale
      const newLocale = getLocale(code);
      Alpine.store('locale', newLocale);
      console.log(`🌐 Language changed to: ${code}`);
    },

    toggle() {
      this.open = !this.open;
    },

    close() {
      this.open = false;
    }
  }));

  // Water intake screen component
  Alpine.data('waterIntakeScreen', () => ({
    selectedGlasses: 0,

    selectGlass(glassNumber: number) {
      // If clicking on the same glass that's the current max, toggle it off
      if (this.selectedGlasses === glassNumber) {
        this.selectedGlasses = glassNumber - 1;
      } else {
        this.selectedGlasses = glassNumber;
      }
    },

    submitSelection() {
      if (this.selectedGlasses > 0) {
        // Store the value (navigation is handled by data-action="next")
        store.set('wq_water_intake', this.selectedGlasses);
      }
    }
  }));
});

// Application initialization
async function boot() {
  console.log('🚀 Lunai Care Web2App Starting...');

  if (window.location.hash === '#success' && (new URLSearchParams(window.location.search)).get('redirect_status') === 'failed') {
    console.error('Setup intent failed');
    window.location.hash = '#checkout';
  }


  // Get preloader element
  const preloader = document.getElementById('preloader');

  try {
    // Show preloader is already visible by default

    // A/B assignment must precede everything that reads the flow (router,
    // progress, preloader) AND the first analytics event, so session_start
    // already carries the ab_* properties.
    initExperiments();

    // Consent-free analytics core: only buffers events in memory (no storage,
    // no network) so session_start and early screen views are captured at
    // their real time and replayed to vendors once consent is granted.
    initAnalytics();

    // Determine initial language (saved or browser-detected)
    const savedLang = store.get<string>('language');
    let initialLang = 'en';
    if (savedLang && locales[savedLang]) {
      initialLang = savedLang;
    } else {
      const browserLang = (navigator.language || '').toLowerCase().split('-')[0];
      if (locales[browserLang]) {
        initialLang = browserLang;
      }
    }

    // Load localization data
    Alpine.store('locale', getLocale(initialLang));

    // Initialize consent management store
    initConsentAlpineData();

    // Initialize Alpine stores
    // Load existing user data from store
    const existingData = store.getAll() || {};
    console.log('Initializing Alpine userData store with:', existingData);
    Alpine.store('userData', Alpine.reactive(existingData));
    Alpine.store('flags', {});

    // Keep Alpine store in sync with regular store
    store.on((state) => {
      const alpineStore = Alpine.store('userData');
      Object.keys(state).forEach(key => {
        if (alpineStore[key] !== state[key]) {
          alpineStore[key] = state[key];
          console.log(`Synced Alpine userData.${key} = ${JSON.stringify(state[key])}`);
        }
      });
    });

    // Start Alpine
    Alpine.start();

    // Get app container
    const appContainer = document.getElementById('app');
    if (!appContainer) {
      console.error('App container not found');
      hidePreloader(preloader);
      return;
    }

    // Wait for consent decision before initializing tracking services
    waitForConsent(async (consent) => {
      if (consent.analytics) {
        // Initialize analytics services that require consent.
        // Identity first: persistent user/session ids may only be minted under
        // the analytics category (a marketing-only grant must not create them).
        enableAnalyticsIdentity();
        // Statsig must init before Amplitude so the owned web_id is established and
        // pushed into Statsig's stableID before Amplitude reads it as device_id.
        await initStatsig();
        await initAmplitude();
        initGoogleTag();
      }

      if (consent.marketing) {
        // Initialize marketing services that require consent
        await initMetaPixel();
      }

      if (!consent.analytics && !consent.marketing) {
        // Declined: the buffered pre-consent events must never be sent
        clearAnalyticsBuffer();
      }
    });

    // Initialize essential services (no consent required) with minimum delay
    await Promise.all([
      // Add minimum display time for preloader (1.5 seconds)
      new Promise(resolve => setTimeout(resolve, 1500))
    ]);

    // Initialize screen controller
    const screenController = new ScreenController(appContainer);

    // Initialize router
    const router = new Router(screenController);

    // Initialize preloader service
    const preloaderService = new PreloaderService();
    preloaderService.initialize(FLOW_CONFIG);

    // Process onload preloads in background (non-blocking)
    preloaderService.processOnloadPreloads().catch(console.error);

    // Provide preloader to router
    router.setPreloaderService(preloaderService);

    // Set up navigation event listeners using PRD pattern
    document.addEventListener('ui:navigate', (event) => {
      const direction = event.detail?.direction;
      if (direction === 'next') {
        router.navigateNext();
      } else if (direction === 'back') {
        router.navigateBack();
      } else if (event.detail?.screenId) {
        router.navigateTo(event.detail.screenId);
      }
    });

    // Provide current screen info to screen controller
    document.addEventListener('app:getCurrentScreen', (event) => {
      event.detail = { screen: router.getCurrentScreen() };
    });

    // Track screen views
    document.addEventListener('app:screenView', (event) => {
      console.log('Screen view:', event.detail);
      // Send to analytics service
    });

    // Handle timer expiration
    document.addEventListener('app:timerExpired', () => {
      handleTimerExpired();
    });

    // Set up back button click handler
    const backButton = document.querySelector('.header-back-button');
    if (backButton) {
      backButton.addEventListener('click', () => {
        router.navigateBack();
      });
    }

    // Start router
    router.init();

    // Hide preloader after everything is initialized
    hidePreloader(preloader);

    // Process afterAppLoaded preloads (non-blocking, after preloader hides)
    preloaderService.processAfterAppLoadedPreloads().catch(console.error);

    // Log initial state
    console.log('📱 App initialized successfully');
    console.log('📊 User data:', store.getAll());
  } catch (error) {
    console.error('Error during initialization:', error);
    // Hide preloader even on error
    hidePreloader(preloader);
  }
}

// Hide preloader with animation
function hidePreloader(preloader: HTMLElement | null) {
  if (preloader) {
    preloader.classList.add('hidden');
    // Remove from DOM after animation completes
    setTimeout(() => {
      preloader.style.display = 'none';
    }, 500);
  }
}

// Handle timer expiration
function handleTimerExpired() {
  // Could show different pricing or messaging
  console.log('⏰ Timer expired - showing regular pricing');
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Export for debugging
window.lunaiDebug = {
  store,
  getState: () => store.getAll(),
  clearState: () => store.clear(),
  getExperiments: () => getActiveExperiments(),
  navigateTo: (screenId) => {
    document.dispatchEvent(new CustomEvent('navigation:to', { detail: { screenId } }));
  }
};
